<template>
  <div class="col-xs-12">
    <steps :filled-steps="filledSteps" />
    <h2 class="title-2 text-center partner-title">
      School Information
    </h2>

    <form @submit.prevent="handleSubmit" class="form-container" novalidate>
      <div class="grid">
        <div class="grid-1">
          <transition name="fade">
            <div class="input-group" :class="{ 'school-not-listed': form.schoolNotListed }">
              <label for="">State</label>
              <select
                v-model="form.state"
                class="input-field"
                @change="$v.form.state.$touch(); fetchSchools();"
                :class="{
                  'success': !$v.form.state.$invalid && $v.form.state.$dirty,
                  'warning': $v.form.state.$invalid && $v.form.state.$dirty
                }"
                :disabled="form.schoolNotListed"
                required
              >
                <option :value="null">
                  State
                </option>
                <option
                  v-for="(state, index) in states"
                  :value="state.id"
                  :key="index"
                >
                  {{state.name}}
                </option>
              </select>
              <span class="span-placeholder">
                <i class="fal fa-city" />
              </span>
              <span
                v-if="!$v.form.state.$invalid && $v.form.state.$dirty"
                class="span-state success"
              >
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>
              <transition name="fade">
                <warning-alert v-if="$v.form.state.$error">
                  This field is required!
                </warning-alert>
              </transition>
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" :class="{ 'school-not-listed': form.schoolNotListed }">
              <label for="">Zip Code</label>
              <select
                v-model="form.zipcode"
                class="input-field"
                @change="$v.form.zipcode.$touch(); fetchSchools();"
                :class="{
                  'success': !$v.form.zipcode.$invalid && $v.form.zipcode.$dirty,
                  'warning': $v.form.zipcode.$invalid && $v.form.zipcode.$dirty
                }"
                :disabled="form.schoolNotListed"
                required
              >
                <option :value="null">
                  Zip Code
                </option>
                <option
                  v-for="(zipcode, index) in computedZipCodes"
                  :value="zipcode.zipcode"
                  :key="index"
                >
                  {{zipcode.zipcode}}
                </option>
              </select>
              <span class="span-placeholder">
                <i class="fal fa-map-pin" aria-hidden="true"></i>
              </span>
              <span
                v-if="!$v.form.zipcode.$invalid && $v.form.zipcode.$dirty"
                class="span-state success"
              >
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>
              <transition name="fade">
                <warning-alert v-if="$v.form.zipcode.$error">
                  This field is required!
                </warning-alert>
              </transition>
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" :class="{ 'school-not-listed': form.schoolNotListed }">
              <label for="">School Name</label>
              <select
                v-model="form.id"
                class="input-field no-fa-icon"
                @change="handleSchoolChange()"
                :class="{
                  'success': form.id
                }"
                :disabled="form.schoolNotListed"
                :required="!form.schoolNotListed"
              >
                <option :value="null">
                  {{ computedSchoolFetchState }}
                </option>
                <option
                  v-for="school in schools"
                  :key="school.id"
                  :value="school.id"
                >
                  {{school.name}}
                </option>
              </select>

              <span v-show="form.id" class="span-state">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>

              <!-- <transition name="fade">
                <WarningAlert v-if="$v.form.id.$error">
                  This field is required!
                </WarningAlert>
              </transition> -->
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" v-if="form.id">
              <label for="">City</label>
              <input
                type="text"
                v-model="form.city"
                class="input-field"
                placeholder="City"
                :class="{ 'success': form.city  }"
                disabled
              >
              <span class="span-placeholder">
                <i class="far fa-building" aria-hidden="true"></i>
              </span>
              <span v-show="form.city" class="span-state">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" v-if="form.id">
              <label for="">School District Name</label>
              <input
                type="text"
                v-model="form.schoolDistrictName"
                class="input-field no-fa-icon"
                placeholder="School District Name no-fa-icon"
                :class="{ 'success': form.schoolDistrictName  }"
                disabled
              >

              <span v-show="form.schoolDistrictName" class="span-state">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>

              <!-- <transition name="fade">
                <warning-alert v-if="$v.form.schoolDistrictName.$error">
                  This field is required!
                </warning-alert>
              </transition> -->
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" v-if="form.id">
              <label for="">School Address Line 1</label>
              <input
                type="text"
                v-model="form.SchoolAddressLineOne"
                class="input-field no-fa-icon"
                placeholder="School Address Line 1"
                :class="{ 'success': form.SchoolAddressLineOne  }"
                disabled
              >

              <span v-show="form.SchoolAddressLineOne" class="span-state">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>

              <!-- <transition name="fade">
                <warning-alert v-if="$v.form.SchoolAddressLineOne.$error">
                  This field is required!
                </warning-alert>
              </transition> -->
            </div>
          </transition>
        </div>

        <div class="grid-2">
          <transition name="fade">
            <div class="input-group" v-if="form.id">
              <label for="">School Address Line 2</label>
              <input
                type="text"
                v-model="form.SchoolAddressLineTwo"
                class="input-field no-fa-icon"
                placeholder="School Address Line 2"
                :class="{ 'success': form.SchoolAddressLineTwo }"
                disabled
              >

              <span v-show="form.SchoolAddressLineTwo" class="span-state">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>
            </div>
          </transition>

          <transition name="fade">
            <div class="input-group" v-if="form.id">
              <label for="">
                Percentage of students enrolled in the National School Lunch Program
              </label>
              <input
                type="number"
                v-model="form.percentage"
                class="input-field no-fa-icon"
                placeholder="20%"
                :class="{
                  'success': form.percentage
                }"
                :min="0"
                :max="100"
                disabled
              >
              <span v-show="form.percentage && form.percentage >= 50" class="span-state span-percentage">
                <i class="fal fa-check" aria-hidden="true"></i>
              </span>

              <!-- <transition name="fade">
                <warning-alert  v-if="$v.form.percentage.$error">
                  This field is required!
                </warning-alert>
              </transition> -->
            </div>
          </transition>

          <div class="input-group">
            <div class="input-radio-group">
              <input type="checkbox" v-model="form.schoolNotListed" @change="handleChangeStatus">
              <span>
                My School is not available in this list
              </span>

              <!-- <span class="inner-circle" :class="{ 'filled-background': form.schoolNotListed }"></span> -->
            </div>
          </div>

          <div class="input-group">
            <p class="paragraph font-weight-light">
              If your school is not listed and has at least 50% of students enrolled in the National School Lunch Program,
              please provide your school name, address, and NSLP % below.
            </p>

            <textarea
              v-model="form.extraInformation"
              cols="30"
              rows="10"
              class="input-field no-fa-icon"
              :disabled="!form.schoolNotListed"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-sm-6 mb-3">
          <a href="/teachers/information" @click="isPrevious = true" class="btn btn-teacher-form btn-previous" :disabled="isSubmitting">
            <span v-show="!isSubmitting || !isPrevious">
              PREVIOUS
            </span>
            <span v-show="isSubmitting && isPrevious">
              <i class="fas fa-spin fa-spinner" />
              PROCESSING
            </span>
          </a>
        </div>
        <div class="col-xs-12 col-sm-6 mb-3">
          <button type="submit" class="btn btn-teacher-form" :disabled="isSubmitting">
            <span v-show="!isSubmitting">
              CONTINUE
            </span>
            <span v-show="isSubmitting">
              <i class="fas fa-spin fa-spinner" />
              PROCESSING
            </span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Steps from './Steps.vue'
import { required, requiredIf } from 'vuelidate/lib/validators'
import WarningAlert from '../components/WarningAlert'
import TeacherService from '../../../services/TeacherService'
import SchoolService from '../../../services/SchoolService'

const service = new TeacherService()

export default {
  name: 'TeacherSchoolInformation',
  components: {
    Steps,
    WarningAlert
  },
  props: {
    teacher: Object,
    grades: Array,
    zipCodes: Array,
    states: Array,
    filledSteps: Array
  },
  data () {
    return {
      form: {
        state: null,
        city: null,
        schoolName: null,
        schoolDistrictName: null,
        SchoolAddressLineOne: null,
        SchoolAddressLineTwo: null,
        zipcode: null,
        percentage: 50,
        schoolNotListed: false,
        extraInformation: null,
        id: null
      },
      isPrevious: false,
      isSubmitting: false,
      schools: [],
      selectedSchool: null,
      fetchIsEmpty: false
    }
  },
  async created () {
    if (!Array.isArray(this.teacher.teacher.teacher_data.school_information)) {
      this.form = this.teacher.teacher.teacher_data.school_information

      await this.fetchSchools()
    }
  },
  validations: {
    form: {
      state: { required },
      // city: { required },
      zipcode: { required },
      // id: { required },
      schoolName: {
        required: requiredIf(function (nestedModel) {
          return !nestedModel.schoolNotListed
        })
      }
      // schoolDistrictName: { required },
      // SchoolAddressLineOne: { required },
      // percentage: {
      //   required: requiredIf(function (nestedModel) {
      //     return !nestedModel.schoolNotListed
      //   })
      // }
    }
  },
  computed: {
    computedZipCodes () {
      const selectedState = this.states.find((state) => state.id === this.form.state)

      if (selectedState === undefined) {
        return []
      }

      return this.zipCodes.filter((zipcode) => zipcode.state === selectedState.slug)
    },
    computedSchoolFetchState () {
      return this.fetchIsEmpty ? 'No Schools Found' : 'Select a School from the list'
    }
  },
  methods: {
    async handleSubmit () {
      this.touchForm()

      if (
        (!this.$v.form.$anyError && this.$v.form.$anyDirty && !this.isSubmitting) ||
        this.form.schoolNotListed
      ) {
        if (!this.form.schoolNotListed && !this.form.id) {
          return null
        }

        this.isSubmitting = true
        const previousUrl = this.isPrevious ? '/teachers/information' : null
        service.save(this.form, '/teachers/saveSchoolInformation', this.isPrevious, previousUrl)
      }
    },
    async fetchSchools () {
      if (this.form.state !== null && this.form.zipcode !== null) {
        const service = new SchoolService()
        const response = await service.fetch(this.form.state, this.form.zipcode)

        this.schools = await response.data
        this.fetchIsEmpty = await this.schools.length === 0
      }
    },
    handleSchoolChange () {
      if (!this.form.id) {
        this.form.schoolName = null
        this.form.schoolDistrictName = null
        this.form.SchoolAddressLineOne = null
        this.form.SchoolAddressLineTwo = null
        this.form.percentage = 0
        return null
      }

      const selectedSchool = this.schools.filter((school) => school.id === this.form.id)[0]
      this.form.schoolName = selectedSchool.name
      this.form.schoolDistrictName = selectedSchool.district
      this.form.SchoolAddressLineOne = selectedSchool.street_address_one
      this.form.SchoolAddressLineTwo = selectedSchool.street_address_two
      this.form.percentage = selectedSchool.nslp_percentage
      this.form.city = selectedSchool.city
      this.form.schoolNotListed = false
    },
    handleChangeStatus () {
      if (this.form.schoolNotListed) {
        this.form.schoolName = null
        this.form.schoolDistrictName = null
        this.form.SchoolAddressLineOne = null
        this.form.SchoolAddressLineTwo = null
        this.form.id = null
        this.form.state = null
        this.form.zipcode = null
      }
    },
    touchForm () {
      if (!this.form.schoolNotListed) {
        // this.$v.form.SchoolAddressLineOne.$touch()
        // this.$v.form.city.$touch()
        // this.$v.form.percentage.$touch()
        // this.$v.form.schoolDistrictName.$touch()
        // this.$v.form.schoolName.$touch()
        this.$v.form.state.$touch()
        this.$v.form.zipcode.$touch()
      }
    }
  }
  // watch: {
  //   selectedSchool: {
  //     handler () {
  //       const type = typeof this.selectedSchool

  //       if (type === 'string') {
  //         this.form.schoolName = this.selectedSchool
  //         this.form.id = null
  //       } else {
  //         this.form.id = this.selectedSchool.id
  //         this.form.schoolName = this.selectedSchool.name
  //         this.form.SchoolAddressLineOne = this.selectedSchool.street_address_one
  //         this.form.SchoolAddressLineTwo = this.selectedSchool.street_address_two
  //         this.form.city = this.selectedSchool.city
  //         this.form.percentage = this.selectedSchool.nslp_percentage
  //         this.form.schoolDistrictName = this.selectedSchool.district
  //       }
  //     }
  //   }
  // }
}
</script>

<style lang="scss" scoped>
.partner-title {
  color: #6D6E71;
  font-size: 30px;
}

.description {
  font-family: 'Roboto Slab';
  font-weight: bold;
  font-size: 22px;
  color: #999999;
  margin-bottom: 1.5Stepsrem;
}

.form-container {
  max-width: 750px;
  margin: 0 auto;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;

  .input-group {
    margin-bottom: 20px;
    display: block;

    label {
      margin-bottom: 10px;
      font-family: 'Rubik';
      font-weight: 300;
      font-size: 18px;
      color: #6d6e71;
    }

    .input-field {
      font-weight: 300;
      font-size: 16px;
      font-family: 'Rubik';
      background-color: #F0F0F0;
      border-color: #6D6E71;
      border-width: 1px;

      box-sizing: border-box;
      padding-left: 45px;

      transition: all ease.2s;

      /* -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none; */
      &:not(.no-fa-icon) {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
      }

      &:disabled {
        // background: #9a9a9a;
        /* background: darken(#f0f0f0, 5%); */
        // color: #FFF;
        opacity: .7;
        font-weight: 400;
        color: #999;
      }
    }

    .input-field.success {
      border-color: #60B661;
    }

    .span-placeholder {
      position: absolute;
      top: 50px;
      left: 20px;
      font-size: 16px;
      color: #999999;
    }

    .span-state {
      position: absolute;
      top: 50px;
      right: 20px;
      font-size: 16px;
      color: #60B661;
    }

    .span-percentage {
      top: 77px;
    }

    .input-radio {
      display: inline;
      margin-right: 45px;
      // margin-bottom: 10px;
      font-family: 'Rubik';
      font-weight: 400;
      font-size: 18px;
      color: #6d6e71;
    }

    .d-block.mx-auto {
      display: block;
      margin: 0 auto;
      margin-bottom: 30px;
      text-align: center;
    }

    input[type=radio] {
      appearance: auto;
    }
  }

  @media(min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 45px;
  }
}

.resource-center-alert {
  border: 1px solid #DDC5A4;
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: #FFF4E5;
  border-radius: 30px;
  margin-bottom: 2rem;

  font-family: 'Rubik';
  font-weight: 300;
  color: #663C00;
  font-size: 14px;
  line-height: 17px;

  p {
    margin-bottom: 1rem;
  }

  a  {
    color: #663C00;
    text-decoration: underline;
  }
}

.mb-0 {
  margin-bottom: 0;
}

.error-alert {
  padding: 1rem;
  background-color: #FFF4E5;
  border: 1px solid #DDC5A4;
  border-radius: 15px;
  font-weight: 600;
  font-size: 14px;
  color: #663C00;
  margin-top: 10px;
  display: grid;
  grid-template-columns: 15% 1fr;
  align-items: center;

  span {
    position: relative !important;
    top: auto !important;
    color: #FFA12E !important;
    left: auto !important;
    text-align: center;
  }
}

.input-radio-group {
  display: grid;
  grid-template-columns: 25px 1fr;
  gap: 15px;

  input[type=checkbox] {
    margin: 0 auto;
    border: 4px solid #6D6E71;
    width: 22px;
    border-radius: 50%;
    transition: all ease .2s;

    &:hover {
      cursor: pointer;
    }

    &:checked {
      background-color: #FE8304;
      box-shadow: inset 0px 0px 0px 3px white;
      border: 4px solid #FE8304;
    }
  }

  span {
    font-family: 'Rubik';
    font-weight: 300;
    font-size: 18px;
    line-height: 22px;
    color: #6d6e71;
  }
}

textarea.input-field {
  height: 185px;
}

.inner-circle {
  display: block;
  width: 8px;
  height: 8px;
  background: transparent;
  border-radius: 10px;
  position: absolute;
  top: 7px;
  left: 8px;
  z-index: -99;
  transition: all ease .2s;

  // @media(min-width: 768px) {
  //   left: 8px;
  // }
  // @media(min-width: 992px) {
  //   left: 9px;
  // }
}

.filled-background {
  background: #FE8304;
}

.school-not-listed {
  @media(max-width: 767px) {
    opacity: 0%;
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// resources/js/views/TeachersForm/Forms/SchoolInformation.vue